/**
 * /preview-kit — internal sanity preview for the Phase 0 editorial primitives.
 *
 * Gated behind dev-mode OR `?kit=show` so it never appears for real visitors.
 * Renders every primitive in both cream and charcoal surface tones with prop
 * variations so the visual revamp can be QA'd at a glance.
 */
import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import {
  ChapterEyebrow,
  DisplayHeadline,
  GoldRule,
  EditorialNumeral,
  GoldFoilButton,
  HairlineButton,
  RotatedCaption,
  FilmGrain,
  Ticker,
  IssueSeal,
  EditorialEmptyState,
} from "@/components/editorial";
import useEditorialReveal from "@/hooks/useEditorialReveal";

function Section({ title, surface, children }) {
  const isDark = surface === "charcoal";
  return (
    <section
      className={`relative ${isDark ? "bg-[color:var(--charcoal)] text-[color:var(--cream)]" : "bg-[color:var(--cream)] text-[color:var(--charcoal)]"}`}
    >
      <FilmGrain opacity={isDark ? 0.06 : 0.035} tone={isDark ? "cool" : "warm"} />
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <ChapterEyebrow chapter="I" label={title} tone={isDark ? "gold" : "gold"} />
        <GoldRule width="3rem" className="mt-4 mb-12" />
        {children}
      </div>
    </section>
  );
}

function Tile({ label, children, dark = false }) {
  return (
    <div
      className={`relative p-8 border ${dark ? "border-[color:var(--gold-foil)]/30" : "border-[color:var(--charcoal)]/12"} flex flex-col items-start gap-4 min-h-[160px]`}
    >
      <span className="ed-eyebrow opacity-70">{label}</span>
      <div className="w-full flex-1 flex flex-wrap items-center gap-4">
        {children}
      </div>
    </div>
  );
}

export default function PreviewKit() {
  const location = useLocation();
  useEditorialReveal();

  const isProd = process.env.NODE_ENV === "production";
  const showOverride =
    new URLSearchParams(location.search).get("kit") === "show";
  if (isProd && !showOverride) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="font-sans">
      {/* Cover banner — uses cover-size display headline + rotated caption */}
      <section className="relative bg-[color:var(--cream-paper)] text-[color:var(--charcoal)] overflow-hidden">
        <FilmGrain opacity={0.05} tone="warm" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-[auto_1fr] gap-8">
          <div className="hidden md:flex items-center">
            <RotatedCaption tone="gold">PHASE 0 · DESIGN SYSTEM</RotatedCaption>
          </div>
          <div>
            <ChapterEyebrow chapter="0" label="Editorial Kit" />
            <GoldRule width="3rem" className="my-4" />
            <DisplayHeadline size="cover">
              A quiet kit for the <em>whole</em> revamp.
            </DisplayHeadline>
            <p className="ed-body-lg max-w-xl mt-6 text-[color:var(--charcoal)]/75">
              Every primitive in <code>src/components/editorial/</code> — rendered
              on both surface tones, with prop variations, so we can verify the
              foundation before Phase 1 ships.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <GoldFoilButton href="#cream">View on cream</GoldFoilButton>
              <HairlineButton href="#charcoal" arrow={false}>View on charcoal</HairlineButton>
            </div>
          </div>
        </div>
      </section>

      {/* CREAM SURFACE — every primitive */}
      <div id="cream" />
      <Section title="On Cream" surface="cream">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Tile label="ChapterEyebrow">
            <ChapterEyebrow chapter="I" label="The Studio" />
            <ChapterEyebrow label="Featured" tone="charcoal" />
          </Tile>
          <Tile label="DisplayHeadline · hero">
            <DisplayHeadline as="h3" size="sub">
              A studio worth <em>arriving</em> to.
            </DisplayHeadline>
          </Tile>
          <Tile label="GoldRule">
            <GoldRule />
            <GoldRule width="6rem" weight={2} tone="foil" />
            <GoldRule width="100%" tone="soft" />
          </Tile>
          <Tile label="EditorialNumeral">
            <EditorialNumeral value={1} size="lg" style="outline" />
            <EditorialNumeral value={2} size="lg" style="solid" />
            <EditorialNumeral value="04" size="md" style="outline" tone="charcoal" />
          </Tile>
          <Tile label="GoldFoilButton">
            <GoldFoilButton>Open your studio</GoldFoilButton>
            <GoldFoilButton size="sm" arrow={false}>Save</GoldFoilButton>
          </Tile>
          <Tile label="HairlineButton">
            <HairlineButton>See the long version</HairlineButton>
            <HairlineButton size="sm" arrow={false}>Skip</HairlineButton>
          </Tile>
          <Tile label="IssueSeal">
            <IssueSeal issue="01" season="Spring" year="2026" />
            <IssueSeal issue="02" season="Autumn" year="2026" size={96} tone="charcoal" />
          </Tile>
          <Tile label="RotatedCaption">
            <div className="h-32 flex items-center">
              <RotatedCaption>CHAPTER 01 · 04 PRODUCTS</RotatedCaption>
            </div>
          </Tile>
          <Tile label="Reveal variants">
            <span className="ed-caption uppercase tracking-[0.22em]" data-reveal="gold-underline">Gold underline</span>
            <span className="ed-body-lg" data-reveal="rise">Rise on reveal</span>
          </Tile>
        </div>

        <div className="mt-12">
          <Ticker items={["Now serving · Allure Image", "Newark, NJ", "12 studios onboarded", "Issue Nº 01 — Spring 2026"]} />
        </div>

        <div className="mt-12 border-t border-[color:var(--charcoal)]/10 pt-6">
          <EditorialEmptyState
            eyebrow="GALLERIES"
            headline={<>Awaiting its <em>first portrait</em>.</>}
            description="Create a gallery to share with your client."
            primary={{ label: "New gallery", href: "#" }}
            secondary={{ label: "Read the gallery guide", href: "#" }}
          />
        </div>
      </Section>

      {/* CHARCOAL SURFACE — every primitive */}
      <div id="charcoal" />
      <Section title="On Charcoal" surface="charcoal">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Tile label="ChapterEyebrow" dark>
            <ChapterEyebrow chapter="II" label="The Studio" />
            <ChapterEyebrow label="Featured" tone="cream" />
          </Tile>
          <Tile label="DisplayHeadline" dark>
            <DisplayHeadline as="h3" size="sub" tone="charcoal">
              One quiet room for the whole <em>craft</em>.
            </DisplayHeadline>
          </Tile>
          <Tile label="GoldRule" dark>
            <GoldRule />
            <GoldRule width="6rem" weight={2} tone="foil" />
          </Tile>
          <Tile label="EditorialNumeral" dark>
            <EditorialNumeral value={1} size="lg" style="outline" tone="gold" />
            <EditorialNumeral value={2} size="lg" style="solid" tone="cream" />
          </Tile>
          <Tile label="GoldFoilButton" dark>
            <GoldFoilButton tone="dark">Enter studio</GoldFoilButton>
          </Tile>
          <Tile label="HairlineButton" dark>
            <HairlineButton tone="dark">Log in</HairlineButton>
          </Tile>
          <Tile label="IssueSeal" dark>
            <IssueSeal issue="01" season="Spring" year="2026" tone="gold" />
            <IssueSeal issue="03" season="Winter" year="2026" size={96} tone="cream" />
          </Tile>
          <Tile label="RotatedCaption" dark>
            <div className="h-32 flex items-center">
              <RotatedCaption tone="gold" side="right">2026 · ISSUE 01</RotatedCaption>
            </div>
          </Tile>
          <Tile label="Reveal variants" dark>
            <span className="ed-caption uppercase tracking-[0.22em]" data-reveal="gold-underline">Gold underline</span>
            <span className="ed-body-lg" data-reveal="rise">Rise on reveal</span>
          </Tile>
        </div>

        <div className="mt-12">
          <Ticker tone="dark" scroll items={["Lumetier · operating system for photographers", "Issue Nº 01", "Spring 2026", "12 studios onboarded", "Allure Image", "NJ"]} />
        </div>

        <div className="mt-12 border-t border-[color:var(--gold-foil)]/20 pt-6">
          <EditorialEmptyState
            tone="charcoal"
            eyebrow="BOOKINGS"
            headline={<>Inbox <em>zero</em>, beautifully.</>}
            description="You're caught up. We will whisper when a new booking arrives."
            primary={{ label: "View calendar", href: "#" }}
            secondary={{ label: "Booking guide", href: "#" }}
          />
        </div>
      </Section>

      <footer className="bg-[color:var(--charcoal)] text-[color:var(--cream)]/70 py-10 text-center">
        <p className="ed-eyebrow">PHASE 0 · DESIGN SYSTEM · {new Date().getFullYear()}</p>
      </footer>
    </main>
  );
}
